import React, { useCallback, useRef, useState } from 'react'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { debounce } from 'lodash'
import { BarChart, Bar, YAxis, XAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts'
import { ChartRefType } from '../../../shared/types/chart-ref-type'
import { ChargingSessionChartTooltip } from './DashboardChartTooltip'
import { getCurrencyValue } from 'src/Libs/e-mobility/shared'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { CUSTOM_TABLET_BREAKPOINT, MetricType } from '../../models/dashboard.model'
import { UnitTypes } from 'src/Libs/types'
import { makeStyles } from 'tss-react/mui'
import { ChartLegend } from './legend/ChartLegend'

const useStyles = makeStyles()(theme => ({
  xAxisTick: {
    '&.recharts-xAxis .recharts-cartesian-axis-tick-value': {
      fontSize: 11,
      fill: theme.palette.shades.shade02,
    },
  },
  yAxisTick: {
    '&.recharts-yAxis .recharts-cartesian-axis-tick-value': {
      fontSize: 11,
      fill: theme.palette.shades.shade02,
    },
  },
}))

interface DashboardChartProps {
  data: { grid: number; solar: number; discharged: number; date: string }[]
  isEdisonConnect: boolean
  metric: MetricType
  currencyCode: string
}

/**
 * Chart component displays statistics chart for emobility dashboard chart.
 * @param - array of grid, solar, discharged energy as number and date as string
 * @param - isEdisonConnect as boolean
 */
export const DashboardChart: React.FC<DashboardChartProps> = ({ data, isEdisonConnect, metric, currencyCode }) => {
  const theme = useTheme()
  const { classes } = useStyles()
  const isMobile = useMediaQuery(theme.breakpoints.down(CUSTOM_TABLET_BREAKPOINT))

  /**
   * The following lines are used to calculate the tooltip "offset". This value is used to set the
   * minimal distance between the mouse pointer and the tooltip. For the best possible user experience,
   * the tooltip should not cover the active bar, but be shown next to it, but also not be too far away,
   * ideally it should hide the bar right next to the active bar.
   *
   * However, since bar width differs greatly between "week" view(7 bars total), "month" view(28-31 bars total)
   * and "year" view(12 bars total), we cannot use a fixed value for "offset", but rather calculate it dynamically.
   *
   * We calculate it by 1. measuring the chart width 2. dividing it by the total number of bars displayed and
   * 3. adding some small additional margin for better layout
   */
  const barChartRef = useRef(null)
  const [chartContainerWidth, setChartContainerWidth] = useState(0)
  const barWidth = chartContainerWidth / data.length
  const tooltipOffset = barWidth / 2 + 4

  const handleChartContainerResize = debounce(
    useCallback(() => {
      if (!barChartRef.current) {
        return
      }

      /**
       * "onResize" callback actually returns the chart width(number) as a callback parameter. However,
       * for calculating the bar size, we need the width of the area to the right of the y-axis. This value
       * can be read from the chart ref itself.
       *
       * It is internally calculated as the full width of the parent container
       * minus the width that the y-axis and the labels for the y-axis took from the left-hand side of the chart.
       */
      const usableBarChartWidth = (barChartRef.current as ChartRefType).state.offset.width

      setChartContainerWidth(usableBarChartWidth)
    }, [setChartContainerWidth]),
    500
  )

  const yAxisTickFormatter = useCallback(
    /** allow "any" since Recharts itself defines the "value" parameter as "any" */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      switch (metric) {
        case MetricType.Energy:
          return getLocalizedNumber(value)
        case MetricType.Cost:
          return getCurrencyValue(value, currencyCode, 0)
      }
    },
    [metric, currencyCode]
  )

  return (
    <Stack height="100%">
      <ResponsiveContainer width="100%" height="100%" onResize={handleChartContainerResize}>
        <BarChart
          margin={{
            top: 25,
          }}
          /** BarChart requires at least one data point in order to display a valid, but empty chart, so mock it here */
          data={data.length === 0 ? [{ grid: 0, solar: 0, discharged: 0, gridCost: 0, solarCost: 0, dischargedCost: 0, date: '' }] : data}
          stackOffset="sign"
          ref={barChartRef}
          barGap={0}
        >
          <CartesianGrid vertical={false} stroke={theme.palette.shades.shade02} strokeWidth={0.4} />
          <Legend verticalAlign="bottom" layout="horizontal" content={<ChartLegend isEdisonConnect={isEdisonConnect} metric={metric} />} />
          <ReferenceLine y={0} stroke={theme.palette.grey.grey02} />
          <XAxis
            dataKey="date"
            interval="preserveStartEnd"
            tickSize={10}
            padding={{ left: 12, right: 12 }}
            stroke={theme.palette.shades.shade02}
            className={classes.xAxisTick}
          />
          <YAxis
            tickSize={10}
            tickFormatter={yAxisTickFormatter}
            tickLine={false}
            axisLine={false}
            className={classes.yAxisTick}
            stroke={theme.palette.shades.shade02}
            unit={metric === MetricType.Energy ? ` ${UnitTypes.kiloWattHour}` : ''}
          />
          {/* position "y: 32, x: undefined" forces tooltip to only move horizontally and have a fixed y-position little below the chart top edge */}
          <Tooltip
            content={<ChargingSessionChartTooltip metric={metric} currencyCode={currencyCode} />}
            offset={tooltipOffset}
            position={{ y: 32 }}
          />

          {/* Energy bars */}
          <Bar
            dataKey="grid"
            stackId="a"
            fill={theme.palette.chart.totalGridChargedBar}
            hide={metric !== MetricType.Energy}
            radius={isMobile ? undefined : [6, 6, 0, 0]}
          />
          <Bar
            dataKey="solar"
            stackId={isMobile ? 'a' : 'b'}
            fill={theme.palette.chart.solarChargedBar}
            hide={metric !== MetricType.Energy}
            radius={isMobile ? undefined : [6, 6, 0, 0]}
          />

          <Bar
            dataKey="discharged"
            stackId={isMobile ? 'a' : 'c'}
            fill={theme.palette.chart.totalDischargedEnergyBackground}
            hide={isEdisonConnect && metric !== MetricType.Energy}
            radius={isMobile ? undefined : [6, 6, 0, 0]}
          />

          {/* Cost bars */}
          <Bar
            dataKey="gridCost"
            stackId="a"
            fill={theme.palette.chart.totalGridChargedBar}
            hide={metric !== MetricType.Cost}
            radius={isMobile ? undefined : [6, 6, 0, 0]}
          />
          <Bar
            dataKey="solarCost"
            stackId={isMobile ? 'a' : 'b'}
            fill={theme.palette.chart.solarChargedBar}
            hide={metric !== MetricType.Cost}
            radius={isMobile ? undefined : [6, 6, 0, 0]}
          />
          <Bar
            dataKey="dischargeCost"
            stackId={isMobile ? 'a' : 'c'}
            fill={theme.palette.chart.totalDischargedEnergyBackground}
            hide={isEdisonConnect && metric !== MetricType.Cost}
            radius={isMobile ? undefined : [6, 6, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Stack>
  )
}
